import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listAllCategories, deleteCategory } from "@/lib/admin.functions";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { PageHeader, type Cat } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/categories/")({
  component: CategoriesListPage,
});

function CategoriesListPage() {
  const list = useServerFn(listAllCategories);
  const del = useServerFn(deleteCategory);
  const [cats, setCats] = useState<Cat[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      setCats((await list({})) as Cat[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const remove = async (id: string) => {
    if (!confirm("Ta bort kategorin?")) return;
    try {
      await del({ data: { id } });
      toast.success("Borttagen");
      await reload();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <>
      <PageHeader title={`Kategorier (${cats.length})`}>
        <Link
          to="/admin/categories/new"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2.5 text-sm font-bold w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" /> Ny kategori
        </Link>
      </PageHeader>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <div className="space-y-2">
          {cats.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 flex-wrap"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold">{c.name}</span>
                  {!c.is_published && (
                    <span className="text-[10px] font-bold uppercase bg-secondary px-2 py-0.5 rounded-full">
                      Utkast
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  /{c.slug} · sortering {c.sort_order}
                  {c.description ? ` · ${c.description}` : ""}
                </p>
              </div>
              <Link
                to="/admin/categories/$id"
                params={{ id: c.id }}
                className="p-2.5 rounded-md bg-secondary hover:bg-secondary/70 min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
                title="Redigera"
                aria-label="Redigera kategori"
              >
                <Pencil className="h-4 w-4" />
              </Link>
              <button
                onClick={() => remove(c.id)}
                className="p-2.5 rounded-md bg-destructive text-destructive-foreground min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
                title="Ta bort"
                aria-label="Ta bort kategori"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {cats.length === 0 && (
            <p className="text-sm text-muted-foreground">Inga kategorier.</p>
          )}
        </div>
      )}
    </>
  );
}
